// features/sales/pages/SalesPage.tsx - COMPLETE FIXED VERSION
/**
 * Sales History Page - Enterprise Edition
 * 
 * Features:
 * - Dashboard stats from reports API
 * - Quick date filters (Today, Yesterday, This Week, This Month, Last Month)
 * - Payment channel breakdown with icons
 * - Advanced search and filtering
 * - Export to CSV
 * - Real-time stats cards
 * - Staff and branch filters
 * - Pagination with page stats
 * - Tax exempt indicators
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
    ClipboardList, Search, Eye, Filter, X, RefreshCw,
    DollarSign, ShoppingCart, TrendingUp, Calendar,
    ChevronLeft, ChevronRight, Download, CreditCard,
    Banknote, Smartphone, Building2, Users, Store,
    Receipt, BarChart3, ArrowUp, ArrowDown, Sparkles,
    Clock, Tag, Percent, ShieldCheck, FileText,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface Sale {
    id: string;
    orderNumber: string;
    customerId: string | null;
    customerName?: string | null;
    cashierId: string;
    cashierName?: string;
    branchId: string;
    branchName?: string;
    shiftId: string | null;
    tableName?: string | null;
    status: 'completed' | 'voided' | 'refunded' | 'parked';
    saleType: 'retail' | 'wholesale' | 'clearance';
    subtotal: string;
    discountTotal: string;
    taxTotal: string;
    grandTotal: string;
    items?: any[];
    payments?: any[];
    notes?: string | null;
    voidReason?: string | null;
    discountReason?: string | null;
    taxExempt?: boolean;
    createdAt: string;
}

interface DashboardData {
    totalSales: string;
    totalTransactions: number;
    averageBasket: string;
    activeShifts: number;
    channels: Array<{ method: string; total: string; count: number }>;
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_STYLES: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    voided: 'bg-rose-100 text-rose-700 border-rose-200',
    refunded: 'bg-amber-100 text-amber-700 border-amber-200',
    parked: 'bg-slate-100 text-slate-600 border-slate-200',
};

const PAYMENT_ICONS: Record<string, React.ElementType> = {
    cash: Banknote,
    bank_transfer: Building2,
    pos_card: CreditCard,
    mobile_money: Smartphone,
};

const QUICK_DATES = [
    {
        label: 'Today',
        get: () => {
            const d = new Date();
            const s = d.toISOString().slice(0, 10);
            return { start: s, end: s };
        }
    },
    {
        label: 'Yesterday',
        get: () => {
            const d = new Date(Date.now() - 86400000);
            const s = d.toISOString().slice(0, 10);
            return { start: s, end: s };
        }
    },
    {
        label: 'This Week',
        get: () => {
            const d = new Date();
            const start = new Date(d.setDate(d.getDate() - d.getDay()));
            return {
                start: start.toISOString().slice(0, 10),
                end: new Date().toISOString().slice(0, 10)
            };
        }
    },
    {
        label: 'This Month',
        get: () => {
            const d = new Date();
            return {
                start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10),
                end: d.toISOString().slice(0, 10)
            };
        }
    },
    {
        label: 'Last Month',
        get: () => {
            const d = new Date();
            return {
                start: new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10),
                end: new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10)
            };
        }
    },
];

const PAGE_SIZE = 25;

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SalesPage() {
    const user = useAuthStore((s) => s.user);
    const navigate = useNavigate();
    const tenantId = user?.tenantId || '';

    // State
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [branchId, setBranchId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [saleType, setSaleType] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState<'desc' | 'asc'>('desc');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [page, setPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);

    // Branches query
    const { data: branchesData } = useQuery({
        queryKey: ['branches-sales', tenantId],
        queryFn: async () => {
            try {
                const response = await apiClient.get(`/tenants/${tenantId}/branches`);
                const data = response.data;
                if (Array.isArray(data)) return data;
                if (data?.data && Array.isArray(data.data)) return data.data;
                return [];
            } catch {
                return [];
            }
        },
        enabled: !!tenantId,
    });

    // Dashboard stats from reports API
    const { data: dashboard, isLoading: dashboardLoading } = useQuery<DashboardData>({
        queryKey: ['sales-dashboard', tenantId, dateRange.start, dateRange.end, branchId],
        queryFn: async () => {
            const params: any = {};
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;
            if (branchId) params.branchId = branchId;
            const response = await apiClient.get(`/tenants/${tenantId}/reports/sales-dashboard`, { params });
            // Handle both wrapped { data: {...} } and unwrapped responses
            return response.data?.data || response.data;
        },
        enabled: !!tenantId,
    });

    // Sales list query
    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey: ['sales', tenantId, search, statusFilter, branchId, paymentMethod, saleType, sortBy, order, dateRange.start, dateRange.end, page],
        queryFn: async () => {
            const params: any = {
                limit: PAGE_SIZE,
                sortBy,
                order,
                offset: (page - 1) * PAGE_SIZE
            };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (branchId) params.branchId = branchId;
            if (paymentMethod) params.paymentMethod = paymentMethod;
            if (saleType) params.saleType = saleType;
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;
            const response = await apiClient.get(`/tenants/${tenantId}/sales`, { params });
            return response.data;
        },
        enabled: !!tenantId,
    });

    // Derived data
    const sales: Sale[] = data?.data || data?.sales || [];
    const totalCount: number = data?.meta?.totalCount || data?.meta?.pagination?.totalCount || 0;
    const totalPages: number = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    // Stats
    const totalSalesValue = sales.reduce((s, sale) => s + parseFloat(sale.grandTotal || '0'), 0);
    const completedCount = sales.filter(s => s.status === 'completed').length;
    const voidedCount = sales.filter(s => s.status === 'voided').length;
    const refundedCount = sales.filter(s => s.status === 'refunded').length;
    const parkedCount = sales.filter(s => s.status === 'parked').length;
    const totalDiscounts = sales.reduce((s, sale) => s + parseFloat(sale.discountTotal || '0'), 0);

    const channels = dashboard?.channels || [];

    // Formatters
    const formatNaira = (val: string | number): string => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const formatCompact = (val: number): string => {
        if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
        if (val >= 1000) return `₦${(val / 1000).toFixed(0)}K`;
        return `₦${val.toFixed(0)}`;
    };

    const formatDate = (d: string): string => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatTime = (d: string): string => {
        if (!d) return '';
        return new Date(d).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
    };

    // Handlers
    const clearFilters = useCallback(() => {
        setSearch('');
        setStatusFilter('');
        setBranchId('');
        setPaymentMethod('');
        setSaleType('');
        setSortBy('createdAt');
        setOrder('desc');
        setDateRange({ start: '', end: '' });
        setPage(1);
    }, []);

    const handleQuickDate = useCallback((qd: typeof QUICK_DATES[0]) => {
        setDateRange(qd.get());
        setPage(1);
    }, []);

    const handleExportCSV = useCallback(() => {
        const headers = 'Order Number,Cashier,Customer,Branch,Total,Items,Discount,Status,Sale Type,Tax Exempt,Date\n';
        const csv = sales.map(s =>
            `${s.orderNumber},"${s.cashierName || ''}","${s.customerName || 'Walk-in'}","${s.branchName || ''}",${s.grandTotal},${s.items?.length || 0},${s.discountTotal || '0'},${s.status},${s.saleType},${s.taxExempt ? 'Yes' : 'No'},"${s.createdAt}"`
        ).join('\n');
        const blob = new Blob([headers + csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales-export-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [sales]);

    const hasActiveFilters = !!(search || statusFilter || branchId || paymentMethod || saleType || dateRange.start || dateRange.end || sortBy !== 'createdAt' || order !== 'desc');
    const branchesList = (branchesData || []) as any[];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                {/* ========== HEADER ========== */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/20">
                            <ClipboardList size={20} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Sales History</h1>
                            <p className="text-xs text-slate-500">
                                {totalCount.toLocaleString()} transactions
                                {dashboard && ` · ${formatCompact(parseFloat(dashboard.totalSales || '0'))} total`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-all disabled:opacity-50"
                            type="button"
                        >
                            <RefreshCw size={14} className={clsx(isFetching && 'animate-spin')} />
                            Refresh
                        </button>
                        <button
                            onClick={handleExportCSV}
                            disabled={sales.length === 0}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-all disabled:opacity-50"
                            type="button"
                        >
                            <Download size={14} /> Export CSV
                        </button>
                    </div>
                </div>

                {/* ========== DASHBOARD STATS ========== */}
                {dashboard && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        {/* Main Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <StatCard
                                label="Total Sales"
                                value={formatCompact(parseFloat(dashboard.totalSales || '0'))}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <StatCard
                                label="Transactions"
                                value={String(dashboard.totalTransactions || 0)}
                                icon={ShoppingCart}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <StatCard
                                label="Avg Basket"
                                value={formatNaira(dashboard.averageBasket || '0')}
                                icon={TrendingUp}
                                gradient="from-purple-500 to-purple-700"
                            />
                            <StatCard
                                label="Active Shifts"
                                value={String(dashboard.activeShifts || 0)}
                                icon={Clock}
                                gradient="from-cyan-500 to-blue-600"
                            />
                        </div>

                        {/* Payment Channel Breakdown */}
                        {channels.length > 0 && (
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <BarChart3 size={16} className="text-slate-400" />
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Payment Channels</p>
                                    </div>
                                    <div className="flex items-center gap-3 overflow-x-auto pb-1">
                                        {channels.map((ch: any) => {
                                            const Icon = PAYMENT_ICONS[ch.method] || Banknote;
                                            const total = parseFloat(ch.total || '0');
                                            const dashboardTotal = parseFloat(dashboard.totalSales || '1');
                                            const pct = dashboardTotal > 0
                                                ? ((total / dashboardTotal) * 100).toFixed(0)
                                                : '0';
                                            return (
                                                <div
                                                    key={ch.method}
                                                    className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl flex-shrink-0 min-w-[140px]"
                                                >
                                                    <div className="p-2 rounded-lg bg-white shadow-sm">
                                                        <Icon size={18} className="text-slate-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] text-slate-400 uppercase font-semibold capitalize">
                                                            {ch.method?.replace(/_/g, ' ')}
                                                        </p>
                                                        <p className="text-sm font-bold text-slate-700">{formatCompact(total)}</p>
                                                        <p className="text-[10px] text-slate-400">{pct}% · {ch.count} txns</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Discount Summary */}
                        {totalDiscounts > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                                <Percent size={16} className="text-amber-500" />
                                <span className="text-amber-700">
                                    Total discounts given: <strong>{formatNaira(totalDiscounts)}</strong>
                                </span>
                                <span className="text-amber-400 text-xs">
                                    ({(totalSalesValue > 0 ? (totalDiscounts / totalSalesValue * 100) : 0).toFixed(1)}% of sales)
                                </span>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* ========== QUICK DATE FILTERS ========== */}
                <div className="flex items-center gap-2 flex-wrap">
                    {QUICK_DATES.map((qd) => {
                        const current = qd.get();
                        const isActive = dateRange.start === current.start && dateRange.end === current.end;
                        return (
                            <button
                                key={qd.label}
                                onClick={() => handleQuickDate(qd)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                                    isActive
                                        ? 'bg-teal-50 text-teal-700 border-teal-200'
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                {qd.label}
                            </button>
                        );
                    })}
                    {(dateRange.start || dateRange.end) && (
                        <button
                            onClick={() => setDateRange({ start: '', end: '' })}
                            className="text-xs text-rose-500 font-medium hover:text-rose-700"
                            type="button"
                        >
                            Clear dates
                        </button>
                    )}
                </div>

                {/* ========== FILTERS ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-2.5">
                    {/* Primary Row */}
                    <div className="flex flex-col lg:flex-row gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by order #, customer name, phone, notes..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 focus:bg-white transition-all"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <div className="flex gap-1 bg-slate-100 rounded-xl p-0.5 overflow-x-auto">
                            {['', 'completed', 'voided', 'refunded', 'parked'].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => { setStatusFilter(s); setPage(1); }}
                                    className={clsx(
                                        'px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all whitespace-nowrap',
                                        statusFilter === s
                                            ? 'bg-white shadow-sm text-slate-900 font-semibold'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {s || 'All'}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                            <Calendar size={14} className="text-slate-400 flex-shrink-0" />
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setPage(1); }}
                                className="px-1.5 py-1 text-xs bg-transparent focus:outline-none"
                            />
                            <span className="text-slate-400 text-xs">to</span>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setPage(1); }}
                                className="px-1.5 py-1 text-xs bg-transparent focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* Secondary Row: Branch, Payment, Channel, Sort & Reset */}
                    <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100 text-xs">
                        {/* Branch Dropdown */}
                        <div className="flex items-center gap-1">
                            <Store size={13} className="text-slate-400" />
                            <select
                                value={branchId}
                                onChange={(e) => { setBranchId(e.target.value); setPage(1); }}
                                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            >
                                <option value="">All Branches</option>
                                {branchesList.map((b: any) => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Payment Method */}
                        <div className="flex items-center gap-1">
                            <CreditCard size={13} className="text-slate-400" />
                            <select
                                value={paymentMethod}
                                onChange={(e) => { setPaymentMethod(e.target.value); setPage(1); }}
                                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            >
                                <option value="">All Payments</option>
                                <option value="cash">Cash</option>
                                <option value="card">Card / POS</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="loyalty_points">Loyalty Points</option>
                                <option value="mobile_money">Mobile Money</option>
                                <option value="split">Split Payment</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Sale Type / Channel */}
                        <div className="flex items-center gap-1">
                            <Tag size={13} className="text-slate-400" />
                            <select
                                value={saleType}
                                onChange={(e) => { setSaleType(e.target.value); setPage(1); }}
                                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            >
                                <option value="">All Channels</option>
                                <option value="retail">Retail POS</option>
                                <option value="wholesale">Wholesale</option>
                                <option value="clearance">Clearance</option>
                            </select>
                        </div>

                        {/* Sort Order */}
                        <div className="flex items-center gap-1">
                            <Filter size={13} className="text-slate-400" />
                            <select
                                value={`${sortBy}-${order}`}
                                onChange={(e) => {
                                    const [sb, o] = e.target.value.split('-');
                                    setSortBy(sb);
                                    setOrder(o as any);
                                    setPage(1);
                                }}
                                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            >
                                <option value="createdAt-desc">Newest First</option>
                                <option value="createdAt-asc">Oldest First</option>
                                <option value="totalAmount-desc">Highest Amount</option>
                                <option value="totalAmount-asc">Lowest Amount</option>
                            </select>
                        </div>

                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="ml-auto text-xs text-rose-600 hover:text-rose-700 font-semibold px-2.5 py-1 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1"
                                type="button"
                            >
                                <X size={12} />
                                Reset Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* ========== CONTENT ========== */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load sales"
                        message="Please try again."
                        onRetry={() => refetch()}
                    />
                ) : sales.length === 0 ? (
                    <EmptyState
                        icon={<ClipboardList size={48} />}
                        title={hasActiveFilters ? "No matching sales" : "No sales yet"}
                        description={hasActiveFilters ? "Try adjusting your filters." : "Sales will appear here once transactions are made from the POS terminal."}
                        action={hasActiveFilters ? { label: 'Clear Filters', onClick: clearFilters } : { label: 'Open POS Terminal', onClick: () => navigate('/pos') }}
                    />
                ) : (
                    <Card className="overflow-hidden">
                        {/* Table Info Bar */}
                        <div className="px-6 py-3 bg-slate-50/50 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <p className="text-xs text-slate-500">
                                Showing <span className="font-semibold text-slate-700">{((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)}</span> of{' '}
                                <span className="font-semibold text-slate-700">{totalCount.toLocaleString()}</span> sales
                            </p>
                            <div className="flex items-center gap-4 text-xs">
                                <span className="text-emerald-600">{completedCount} completed</span>
                                {voidedCount > 0 && <span className="text-rose-500">{voidedCount} voided</span>}
                                {refundedCount > 0 && <span className="text-amber-500">{refundedCount} refunded</span>}
                                {parkedCount > 0 && <span className="text-slate-500">{parkedCount} parked</span>}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Cashier</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Customer</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Branch</th>
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Items</th>
                                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-16">View</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {sales.map((sale) => (
                                        <motion.tr
                                            key={sale.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="group hover:bg-slate-50 cursor-pointer transition-colors"
                                            onClick={() => navigate(`/sales/${sale.id}`)}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-sm font-mono font-medium text-slate-800">{sale.orderNumber}</p>
                                                    {sale.taxExempt && (
                                                        <span title="Tax Exempt">
                                                            <ShieldCheck size={12} className="text-blue-400" />
                                                        </span>
                                                    )}
                                                </div>
                                                {sale.tableName && (
                                                    <p className="text-[10px] text-slate-400">Table: {sale.tableName}</p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                                    <Users size={12} className="text-slate-400 flex-shrink-0" />
                                                    <span className="truncate max-w-[120px]">{sale.cashierName || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 hidden lg:table-cell text-sm text-slate-500">
                                                {sale.customerName || 'Walk-in'}
                                            </td>
                                            <td className="px-4 py-3 hidden xl:table-cell text-sm text-slate-500">
                                                <div className="flex items-center gap-1.5">
                                                    <Store size={12} className="text-slate-400 flex-shrink-0" />
                                                    <span className="truncate max-w-[100px]">{sale.branchName || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <span className="text-sm font-semibold text-slate-800">
                                                    {formatNaira(sale.grandTotal)}
                                                </span>
                                                {sale.discountTotal && parseFloat(sale.discountTotal) > 0 && (
                                                    <p className="text-[10px] text-amber-500">-{formatNaira(sale.discountTotal)}</p>
                                                )}
                                                {sale.payments && sale.payments.length > 0 && (
                                                    <div className="flex items-center justify-end gap-1 mt-0.5">
                                                        {sale.payments.slice(0, 3).map((p: any) => {
                                                            const Icon = PAYMENT_ICONS[p.paymentMethod] || Banknote;
                                                            return (
                                                                <span
                                                                    key={p.id}
                                                                    title={p.paymentMethod?.replace(/_/g, ' ')}
                                                                >
                                                                    <Icon size={10} className="text-slate-400" />
                                                                </span>
                                                            );
                                                        })}
                                                        {sale.payments.length > 3 && (
                                                            <span className="text-[9px] text-slate-400">
                                                                +{sale.payments.length - 3}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center hidden sm:table-cell">
                                                <Badge variant="neutral" size="sm">
                                                    {sale.items?.length || 0}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-center hidden md:table-cell">
                                                <span className={clsx(
                                                    'inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full capitalize',
                                                    sale.saleType === 'retail'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : sale.saleType === 'wholesale'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : 'bg-amber-100 text-amber-700'
                                                )}>
                                                    {sale.saleType}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={clsx(
                                                    'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border',
                                                    STATUS_STYLES[sale.status] || ''
                                                )}>
                                                    {sale.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right hidden sm:table-cell">
                                                <div className="text-sm text-slate-600">{formatDate(sale.createdAt)}</div>
                                                <div className="text-xs text-slate-400">{formatTime(sale.createdAt)}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => navigate(`/sales/${sale.id}`)}
                                                    className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-teal-500 transition-colors"
                                                    type="button"
                                                >
                                                    <Eye size={15} />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                                <p className="text-sm text-slate-500">
                                    Page {page} of {totalPages}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(1)}
                                        disabled={page === 1}
                                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100 transition-colors"
                                        type="button"
                                    >
                                        <ChevronLeft size={14} className="rotate-180" />
                                        <ChevronLeft size={14} className="rotate-180 -ml-2" />
                                    </button>
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-30 hover:bg-slate-100 font-medium transition-colors"
                                        type="button"
                                    >
                                        <ChevronLeft size={14} /> Prev
                                    </button>
                                    <span className="px-3 py-2 text-sm font-medium text-slate-600">
                                        {page} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page >= totalPages}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-30 hover:bg-slate-100 font-medium transition-colors"
                                        type="button"
                                    >
                                        Next <ChevronRight size={14} />
                                    </button>
                                    <button
                                        onClick={() => setPage(totalPages)}
                                        disabled={page >= totalPages}
                                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100 transition-colors"
                                        type="button"
                                    >
                                        <ChevronRight size={14} />
                                        <ChevronRight size={14} className="-ml-2" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}

// ============================================================
// STAT CARD COMPONENT
// ============================================================

const StatCard: React.FC<{
    label: string;
    value: string;
    icon: React.ElementType;
    gradient: string;
    warning?: boolean;
}> = ({ label, value, icon: Icon, gradient, warning }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        className={clsx(
            'bg-white rounded-xl border shadow-sm p-4 hover:shadow-md transition-all duration-300',
            warning ? 'border-amber-300' : 'border-slate-200'
        )}
    >
        <div className="flex items-center justify-between">
            <div className="min-w-0">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider truncate">{label}</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5 truncate">{value}</p>
            </div>
            <div className={`p-2.5 rounded-lg bg-gradient-to-br ${gradient} shadow-md flex-shrink-0`}>
                <Icon className="w-4 h-4 text-white" />
            </div>
        </div>
    </motion.div>
);